import { useMemo, useState, type ReactNode } from 'react';
import {
  Card,
  Col,
  Row,
  Statistic,
  Typography,
  Spin,
  Alert,
  Segmented,
  Result,
} from 'antd';
import {
  DollarOutlined,
  ShoppingCartOutlined,
  InboxOutlined,
  WarningOutlined,
  ToolOutlined,
  SafetyCertificateOutlined,
  AlertOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import { useOwnerDashboard } from '@hooks/useAnalytics';
import { useAuth } from '@stores/AuthContext';
import SalesDashboardPage from './SalesDashboardPage';
import WarehouseDashboardPage from './WarehouseDashboardPage';
import ServiceDashboardPage from './ServiceDashboardPage';
import { mdGetAllowedDashboardTabs, type DashboardTab } from './dashboardAccess';

interface OwnerDashboardPageProps {
  onNavigate?: (mdModule: string) => void;
}

export default function OwnerDashboardPage({ onNavigate }: OwnerDashboardPageProps) {
  const { user } = useAuth();
  const mdAllowedTabs = useMemo(() => mdGetAllowedDashboardTabs(user?.roles ?? []), [user?.roles]);
  const [mdSelectedTab, setMdSelectedTab] = useState<DashboardTab | null>(null);
  // Панель, недоступна ролі, не монтується взагалі — інакше її запит отримав би 403
  const mdActiveTab = mdAllowedTabs.find((mdTab) => mdTab.value === mdSelectedTab)?.value
    ?? mdAllowedTabs[0]?.value;

  if (!mdActiveTab) {
    return (
      <Result
        status="info"
        title="Для вашої ролі немає аналітичних панелей"
        subTitle="Оберіть потрібний розділ у меню ліворуч."
      />
    );
  }

  // Перемикач має сенс лише з кількома панелями
  const mdSwitcher = mdAllowedTabs.length > 1 ? (
    <Segmented
      value={mdActiveTab}
      onChange={(mdValue) => setMdSelectedTab(mdValue as DashboardTab)}
      options={mdAllowedTabs.map((mdTab) => ({ label: mdTab.label, value: mdTab.value }))}
      size="large"
    />
  ) : null;

  if (mdActiveTab === 'general') {
    return <GeneralDashboard switcher={mdSwitcher} onNavigate={onNavigate} />;
  }

  return (
    <>
      {mdSwitcher && <div style={{ marginBottom: 16 }}>{mdSwitcher}</div>}
      {mdActiveTab === 'sales' && <SalesDashboardPage />}
      {mdActiveTab === 'warehouse' && <WarehouseDashboardPage />}
      {mdActiveTab === 'service' && <ServiceDashboardPage />}
    </>
  );
}

interface GeneralDashboardProps {
  switcher: ReactNode;
  onNavigate?: (mdModule: string) => void;
}

/** Зведені KPI власника — GET /api/analytics/owner-dashboard (модуль Admin) */
function GeneralDashboard({ switcher, onNavigate }: GeneralDashboardProps) {
  const { data: mdData, isLoading: mdIsLoading } = useOwnerDashboard();

  const mdHandleCardClick = (mdModule: string) => {
    if (onNavigate) {
      onNavigate(mdModule);
    }
  };

  // Заголовок із перемикачем — і під час завантаження, щоб можна було перейти на іншу панель
  const mdHeader = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        Панель керівника
      </Typography.Title>
      {switcher}
    </div>
  );

  if (mdIsLoading) {
    return (
      <>
        {mdHeader}
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </>
    );
  }

  if (!mdData) {
    return (
      <>
        {mdHeader}
        <Alert type="warning" message="Дані дашборду недоступні" showIcon />
      </>
    );
  }

  const mdGrowthColor = mdData.revenueGrowthPercent >= 0 ? '#3f8600' : '#cf1322';
  const mdGrowthIcon = mdData.revenueGrowthPercent >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />;

  return (
    <>
      {mdHeader}

      {/* Row 1: Revenue, Margin, Active SO, Active PO */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card
            hoverable
            onClick={() => mdHandleCardClick('sales-orders')}
            style={{ cursor: 'pointer' }}
          >
            <Statistic
              title="Виручка"
              value={mdData.totalRevenue}
              precision={0}
              prefix={<DollarOutlined />}
              suffix="UAH"
            />
            <div style={{ marginTop: 8, color: mdGrowthColor, fontSize: 13 }}>
              {mdGrowthIcon}{' '}
              {mdData.revenueGrowthPercent >= 0 ? '+' : ''}
              {mdData.revenueGrowthPercent.toFixed(1)}% до попереднього місяця
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card
            hoverable
            onClick={() => mdHandleCardClick('fin-ar')}
            style={{ cursor: 'pointer' }}
          >
            <Statistic
              title="Маржа"
              value={mdData.grossMargin}
              precision={1}
              prefix={<RiseOutlined />}
              suffix="%"
              valueStyle={{ color: mdData.grossMargin >= 20 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card
            hoverable
            onClick={() => mdHandleCardClick('sales-orders')}
            style={{ cursor: 'pointer' }}
          >
            <Statistic
              title="Активні замовлення (SO)"
              value={mdData.activeSalesOrders}
              prefix={<ShoppingCartOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card
            hoverable
            onClick={() => mdHandleCardClick('purch-orders')}
            style={{ cursor: 'pointer' }}
          >
            <Statistic
              title="Активні закупівлі (PO)"
              value={mdData.activePurchaseOrders}
              prefix={<ShoppingCartOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Row 2: Stock Value, Expiring Batches, Receivables, Overdue Receivables */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card
            hoverable
            onClick={() => mdHandleCardClick('warehouse')}
            style={{ cursor: 'pointer' }}
          >
            <Statistic
              title="Вартість складу"
              value={mdData.totalStockValue}
              precision={0}
              prefix={<InboxOutlined />}
              suffix="UAH"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card
            hoverable
            onClick={() => mdHandleCardClick('warehouse')}
            style={{ cursor: 'pointer' }}
          >
            <Statistic
              title="Партії що закінчуються"
              value={mdData.expiringBatchesCount}
              prefix={<WarningOutlined />}
              valueStyle={{ color: mdData.expiringBatchesCount > 0 ? '#faad14' : undefined }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card
            hoverable
            onClick={() => mdHandleCardClick('fin-ar')}
            style={{ cursor: 'pointer' }}
          >
            <Statistic
              title="Дебіторка всього"
              value={mdData.totalReceivables}
              precision={0}
              prefix={<DollarOutlined />}
              suffix="UAH"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card
            hoverable
            onClick={() => mdHandleCardClick('fin-ar')}
            style={{ cursor: 'pointer' }}
          >
            <Statistic
              title="Прострочена дебіторка"
              value={mdData.overdueReceivables}
              precision={0}
              prefix={<WarningOutlined />}
              suffix="UAH"
              valueStyle={{ color: mdData.overdueReceivables > 0 ? '#cf1322' : '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Row 3: Service Requests, SLA Breached, Expiring Registrations, Active Recalls */}
      <Row gutter={[16, 16]}>
        <Col span={6}>
          <Card
            hoverable
            onClick={() => mdHandleCardClick('svc-requests')}
            style={{ cursor: 'pointer' }}
          >
            <Statistic
              title="Відкриті сервісні заявки"
              value={mdData.openServiceRequests}
              prefix={<ToolOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card
            hoverable
            onClick={() => mdHandleCardClick('svc-sla')}
            style={{ cursor: 'pointer' }}
          >
            <Statistic
              title="SLA порушено"
              value={mdData.slaBreachedCount}
              prefix={<AlertOutlined />}
              valueStyle={{ color: mdData.slaBreachedCount > 0 ? '#cf1322' : '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card
            hoverable
            onClick={() => mdHandleCardClick('reg-documents')}
            style={{ cursor: 'pointer' }}
          >
            <Statistic
              title="Реєстрації що закінчуються"
              value={mdData.expiringRegistrations}
              prefix={<SafetyCertificateOutlined />}
              valueStyle={{ color: mdData.expiringRegistrations > 0 ? '#faad14' : undefined }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card
            hoverable
            onClick={() => mdHandleCardClick('reg-recalls')}
            style={{ cursor: 'pointer' }}
          >
            <Statistic
              title="Активні recalls"
              value={mdData.activeRecalls}
              prefix={<AlertOutlined />}
              valueStyle={{ color: mdData.activeRecalls > 0 ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
}
